'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { IBM_Plex_Mono } from 'next/font/google'
import { faqAnchor } from '@/lib/faq-anchor'
import './site-faq.css'

const plexMono = IBM_Plex_Mono({
  subsets: ['latin'],
  weight: ['400', '500', '600'],
})

export type SiteFaqItem = {
  question: string
  answer: string
}

export default function SiteFaq({
  title,
  faqs,
  ctaHref = '#get-quote',
  ctaLabel = 'Ask me something else',
  tone = 'light',
}: {
  title: ReactNode
  faqs: readonly SiteFaqItem[]
  ctaHref?: string
  ctaLabel?: string
  tone?: 'light' | 'dark'
}) {
  const [openId, setOpenId] = useState(() => faqAnchor(faqs[0]?.question ?? ''))

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace(/^#/, '')
      if (hash && faqs.some((faq) => faqAnchor(faq.question) === hash)) {
        setOpenId(hash)
      }
    }
    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => window.removeEventListener('hashchange', applyHash)
  }, [faqs])

  return (
    <section
      id="faq"
      className={`site-faq${tone === 'dark' ? ' site-faq--dark' : ''}`}
      aria-labelledby="site-faq-title"
      style={{ ['--mono' as string]: plexMono.style.fontFamily }}
    >
      <div className="site-faq-wrap">
        <div className="site-faq-panel">
          <aside className="site-faq-aside">
            <p className="site-faq-eyebrow">FAQ</p>
            <h2 id="site-faq-title" className="site-faq-title">
              {title}
            </h2>
            <a className="site-faq-cta" href={ctaHref}>
              {ctaLabel}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M14 5l7 7-7 7M21 12H3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </aside>

          <div className="site-faq-list">
            {faqs.map((faq, i) => {
              const anchor = faqAnchor(faq.question)
              const open = openId === anchor
              return (
                <details
                  key={faq.question}
                  id={anchor}
                  className={`site-faq-item${open ? ' is-open' : ''}`}
                  open={open}
                >
                  <summary
                    className="site-faq-q"
                    onClick={(event) => {
                      event.preventDefault()
                      setOpenId(open ? '' : anchor)
                    }}
                  >
                    <span className="site-faq-n" aria-hidden>{String(i + 1).padStart(2, '0')}</span>
                    <h3 className="site-faq-qt">{faq.question}</h3>
                    <span className="site-faq-icon" aria-hidden />
                  </summary>
                  <div className="site-faq-a" id={`${anchor}-a`}>
                    <div className="site-faq-a-inner">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                </details>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
